/**
 * usePullToRefresh — iOS-style pull-down-to-reload gesture.
 *
 * Returns { pullY, refreshing } so the page can render a tiny
 * indicator that follows the finger. Pass:
 *   onRefresh: async () => { await loadStuff() }
 *   scrollRef: optional ref to a custom scroll container; falls
 *              back to window scrolling
 *   threshold: px past which the gesture commits (default 70)
 *   enabled:   short-circuit the listeners
 *
 * Only fires when the scroller is at the very top — won't conflict
 * with normal in-page scrolling. Also bails if the user starts
 * dragging while we're already refreshing.
 *
 * Why a custom hook instead of a library: framer-motion + a PTR lib
 * is overkill for ~80 lines of vanilla touch events. This stays
 * dependency-free and behaves well on iOS Safari (which loves to
 * fight with passive-listener prevention).
 */
import { useEffect, useRef, useState } from 'react';

export const usePullToRefresh = ({
  onRefresh,
  scrollRef = null,
  threshold = 70,
  enabled = true,
}) => {
  const [state, setState] = useState({ pullY: 0, refreshing: false });
  const stateRef = useRef(state);
  stateRef.current = state;
  const onRefreshRef = useRef(onRefresh);
  onRefreshRef.current = onRefresh;
  const startYRef = useRef(null);

  useEffect(() => {
    if (!enabled) return undefined;

    const target = scrollRef?.current || window;
    const getScrollTop = () => {
      if (scrollRef?.current) return scrollRef.current.scrollTop;
      return window.scrollY || document.documentElement.scrollTop || 0;
    };

    const onTouchStart = (e) => {
      if (stateRef.current.refreshing) return;
      if (getScrollTop() > 0) return;
      startYRef.current = e.touches[0].clientY;
    };

    const onTouchMove = (e) => {
      if (startYRef.current == null || stateRef.current.refreshing) return;
      if (getScrollTop() > 0) {
        // User scrolled away from the top mid-pull — reset.
        startYRef.current = null;
        if (stateRef.current.pullY !== 0) {
          setState({ pullY: 0, refreshing: false });
        }
        return;
      }
      const delta = e.touches[0].clientY - startYRef.current;
      if (delta > 0) {
        // Resistance: divide raw delta so the indicator feels rubbery.
        const eased = Math.min(delta * 0.5, threshold * 1.6);
        setState({ pullY: eased, refreshing: false });
        if (e.cancelable) e.preventDefault();
      }
    };

    const onTouchEnd = async () => {
      if (startYRef.current == null) return;
      startYRef.current = null;
      const py = stateRef.current.pullY;
      if (py >= threshold) {
        setState({ pullY: threshold, refreshing: true });
        try {
          await onRefreshRef.current?.();
        } finally {
          setState({ pullY: 0, refreshing: false });
        }
      } else if (py !== 0) {
        setState({ pullY: 0, refreshing: false });
      }
    };

    target.addEventListener('touchstart', onTouchStart, { passive: true });
    target.addEventListener('touchmove', onTouchMove, { passive: false });
    target.addEventListener('touchend', onTouchEnd);
    target.addEventListener('touchcancel', onTouchEnd);
    return () => {
      target.removeEventListener('touchstart', onTouchStart);
      target.removeEventListener('touchmove', onTouchMove);
      target.removeEventListener('touchend', onTouchEnd);
      target.removeEventListener('touchcancel', onTouchEnd);
    };
  }, [enabled, threshold, scrollRef]);

  return state;
};

export default usePullToRefresh;
